import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { latestArticles } from '@/mocks/home';

export default function CTASection() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [statusMsg, setStatusMsg] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setStatus('success');
      setStatusMsg('订阅成功！感谢您的关注。');
      setEmail('');
      return;
    }
    const formData = new FormData(form);
    try {
      const res = await fetch('https://readdy.ai/api/form/d9mu054it35ii3jdllug', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      const code = (parsed as Record<string, string> | null)?.code;
      if (res.ok && code === 'OK') {
        setStatus('success');
        setStatusMsg('订阅成功！感谢您的关注。');
        setEmail('');
      } else {
        const serverMsg = (parsed as Record<string, string> | null)?.meta?.message
          || (parsed as Record<string, string> | null)?.message
          || text
          || '订阅失败，请稍后重试';
        setStatus('error');
        setStatusMsg(serverMsg.includes('spam') ? '订阅失败，请稍后重试' : serverMsg);
      }
    } catch {
      setStatus('error');
      setStatusMsg('网络错误，请检查连接后重试');
    }
  };

  return (
    <>
      <section className="py-14 md:py-20 px-4 md:px-6 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10">
            <div className="lg:col-span-1">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-3">
                最新资讯
              </h2>
              <p className="text-sm text-foreground-600 mb-6">
                关注茄子视频，获取最新的<strong>影视平台推荐</strong>和观影指南
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-1 text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                了解更多关于我们 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {latestArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group bg-background-100 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="aspect-[3/2] overflow-hidden bg-background-200">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-400"
                      title={article.title}
                      loading="lazy"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs bg-secondary-50 text-secondary-700 px-2 py-0.5 rounded-full whitespace-nowrap">
                        {article.categoryName}
                      </span>
                      <span className="text-xs text-foreground-400 whitespace-nowrap">{article.date}</span>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-2 mb-2">
                      {article.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2">{article.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-14 md:py-20 px-4 md:px-6 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://readdy.ai/api/search-image?query=Warm%20cinematic%20abstract%20gradient%20background%20with%20soft%20golden%20light%20rays%20and%20subtle%20film%20texture%2C%20elegant%20artistic%20design%2C%20minimalist%20and%20sophisticated%20streaming%20platform%20backdrop&width=1600&height=600&seq=cta-qiezi-bg&orientation=landscape')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background-950/80 to-background-950/60"></div>

        <div className="relative z-10 max-w-2xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white font-heading mb-3">
            订阅茄子视频资讯
          </h2>
          <p className="text-sm md:text-base text-white/70 mb-8">
            第一时间获取最新<strong>视频平台推荐</strong>和热门影视资讯，不错过任何好片
          </p>
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-readdy-form="">
            <div className="anti-spam-field">
              <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
            </div>
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="输入您的邮箱地址"
              required
              className="flex-1 px-4 py-3 rounded-lg bg-background-50/15 backdrop-blur-sm text-white placeholder-white/40 border border-white/20 focus:outline-none focus:border-primary-400 text-sm"
            />
            <button
              type="submit"
              className="px-6 py-3 bg-primary-500 text-background-50 font-semibold rounded-lg text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              立即订阅
            </button>
          </form>
          {status !== 'idle' && (
            <p className={`mt-3 text-sm ${status === 'success' ? 'text-accent-400' : 'text-red-400'}`}>
              {statusMsg}
            </p>
          )}
        </div>
      </section>
    </>
  );
}